import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router"
import {
  Badge,
  Box,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  Spinner,
  Table,
  Tabs,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { FiBriefcase, FiCalendar, FiUsers, FiAlertCircle, FiExternalLink } from "react-icons/fi"
import { Dashboard, type ClientSummary, type ClientNoRecentService, type ClientBudgetRisk, type ProviderUserSummary } from "@/client"

export const Route = createFileRoute("/_layout/provider/providerdashboard")({
  component: ProviderDashboard,
})

// ── Stat card ─────────────────────────────────────────────────────────────────

const StatCard = ({
  label,
  value,
  icon: Icon,
  colorPalette = "blue",
}: {
  label: string
  value: number
  icon: React.ElementType
  colorPalette?: string
}) => (
  <Box
    borderWidth="1px"
    borderRadius="lg"
    p={5}
    bg="white"
    display="flex"
    alignItems="center"
    gap={4}
  >
    <Flex
      w={10}
      h={10}
      borderRadius="md"
      bg={`${colorPalette}.subtle`}
      color={`${colorPalette}.600`}
      align="center"
      justify="center"
      flexShrink={0}
    >
      <Icon size={20} />
    </Flex>
    <Box>
      <Text fontSize="2xl" fontWeight="bold" lineHeight={1}>{value}</Text>
      <Text fontSize="sm" color="gray.500">{label}</Text>
    </Box>
  </Box>
)

// ── Alert section ─────────────────────────────────────────────────────────────

const AlertSection = ({
  title,
  icon: Icon,
  colorPalette = "orange",
  children,
}: {
  title: string
  icon: React.ElementType
  colorPalette?: string
  children: React.ReactNode
}) => (
  <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
    <Flex
      px={4}
      py={3}
      bg={`${colorPalette}.subtle`}
      align="center"
      gap={2}
      borderBottomWidth="1px"
      borderColor={`${colorPalette}.100`}
    >
      <Icon size={14} color={`var(--chakra-colors-${colorPalette}-600)`} />
      <Text fontWeight="semibold" fontSize="sm" color={`${colorPalette}.800`}>
        {title}
      </Text>
    </Flex>
    <Box>{children}</Box>
  </Box>
)

// ── No recent service table ───────────────────────────────────────────────────

const NoRecentServiceList = ({ clients }: { clients: ClientNoRecentService[] }) => {
  const navigate = useNavigate()
  const router = useRouter()

  const clientHref = (id: string) =>
    router.buildLocation({ to: "/provider/providerclients/edit", search: { providerClientId: id } }).href

  if (clients.length === 0)
    return <Text fontSize="sm" color="green.600" px={4} py={3}>✓ All clear</Text>

  return (
    <Table.Root size="sm">
      <Table.Header>
        <Table.Row bg="gray.50">
          <Table.ColumnHeader>Client</Table.ColumnHeader>
          <Table.ColumnHeader>Service</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">Days Ago</Table.ColumnHeader>
          <Table.ColumnHeader w="36px" />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {clients.map((c) => (
          <Table.Row key={`${c.id}-${c.serviceItemId}`} _hover={{ bg: "gray.50" }}>
            <Table.Cell>
              <a
                href={clientHref(c.id)}
                onClick={(e) => {
                  e.preventDefault()
                  navigate({ to: "/provider/providerclients/edit", search: { providerClientId: c.id } })
                }}
                style={{ textDecoration: "none" }}
              >
                <Text fontSize="sm" color="blue.600" cursor="pointer">{c.displayName}</Text>
                <Text fontSize="xs" color="gray.400">{c.providerClientId}</Text>
              </a>
            </Table.Cell>
            <Table.Cell>
              <Text fontSize="sm">{c.serviceItemName}</Text>
              <Text fontSize="xs" color="gray.400">every {c.recommendedIntervalDays}d</Text>
            </Table.Cell>
            <Table.Cell textAlign="right">
              <Badge colorPalette="orange" size="sm">
                {c.lastServiceDaysAgo == null ? "Never" : `${c.lastServiceDaysAgo}d ago`}
              </Badge>
            </Table.Cell>
            <Table.Cell textAlign="right" w="36px">
              <a
                href={clientHref(c.id)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in new tab"
                onClick={(e) => e.stopPropagation()}
                style={{ color: "var(--chakra-colors-gray-400)", display: "flex", alignItems: "center", padding: "4px" }}
              >
                <FiExternalLink size={12} />
              </a>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}

// ── Client list table ─────────────────────────────────────────────────────────

const ClientList = ({ clients }: { clients: ClientSummary[] }) => {
  const navigate = useNavigate()
  const router = useRouter()

  const clientHref = (id: string) =>
    router.buildLocation({ to: "/provider/providerclients/edit", search: { providerClientId: id } }).href

  if (clients.length === 0) {
    return (
      <Text fontSize="sm" color="green.600" px={4} py={3}>
        ✓ All clear
      </Text>
    )
  }
  return (
    <Table.Root size="sm">
      <Table.Body>
        {clients.map((c) => (
          <Table.Row key={c.id} _hover={{ bg: "gray.50" }}>
            <Table.Cell>
              <a
                href={clientHref(c.id)}
                onClick={(e) => {
                  e.preventDefault()
                  navigate({ to: "/provider/providerclients/edit", search: { providerClientId: c.id } })
                }}
                style={{ textDecoration: "none" }}
              >
                <Text fontSize="sm" color="blue.600" cursor="pointer">{c.displayName}</Text>
                <Text fontSize="xs" color="gray.400">{c.providerClientId}</Text>
              </a>
            </Table.Cell>
            <Table.Cell textAlign="right" w="36px">
              <a
                href={clientHref(c.id)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in new tab"
                onClick={(e) => e.stopPropagation()}
                style={{ color: "var(--chakra-colors-gray-400)", display: "flex", alignItems: "center", padding: "4px" }}
              >
                <FiExternalLink size={12} />
              </a>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}

// ── Budget risk table ─────────────────────────────────────────────────────────

const BudgetRiskList = ({ clients }: { clients: ClientBudgetRisk[] }) => {
  const navigate = useNavigate()
  const router = useRouter()

  const clientHref = (id: string) =>
    router.buildLocation({ to: "/provider/providerclients/edit", search: { providerClientId: id } }).href

  if (clients.length === 0)
    return <Text fontSize="sm" color="green.600" px={4} py={3}>✓ All clients on track</Text>

  return (
    <Table.Root size="sm">
      <Table.Header>
        <Table.Row bg="gray.50">
          <Table.ColumnHeader>Client</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">Used</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">Remaining</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">Days Left</Table.ColumnHeader>
          <Table.ColumnHeader w="36px" />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {clients.map((c) => (
          <Table.Row key={c.id} _hover={{ bg: "gray.50" }}>
            <Table.Cell>
              <a
                href={clientHref(c.id)}
                onClick={(e) => {
                  e.preventDefault()
                  navigate({ to: "/provider/providerclients/edit", search: { providerClientId: c.id } })
                }}
                style={{ textDecoration: "none" }}
              >
                <Text fontSize="sm" color="blue.600" cursor="pointer">{c.displayName}</Text>
                <Text fontSize="xs" color="gray.400">{c.providerClientId}</Text>
              </a>
            </Table.Cell>
            <Table.Cell textAlign="right">
              <Badge colorPalette={c.percentUsed < 20 ? "red" : "orange"} size="sm">
                {c.percentUsed}%
              </Badge>
            </Table.Cell>
            <Table.Cell textAlign="right">
              <Text fontSize="sm">${c.remaining.toLocaleString()}</Text>
            </Table.Cell>
            <Table.Cell textAlign="right">
              <Badge colorPalette={c.daysLeft <= 14 ? "red" : "orange"} variant="subtle" size="sm">
                {c.daysLeft}d
              </Badge>
            </Table.Cell>
            <Table.Cell textAlign="right" w="36px">
              <a
                href={clientHref(c.id)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in new tab"
                onClick={(e) => e.stopPropagation()}
                style={{ color: "var(--chakra-colors-gray-400)", display: "flex", alignItems: "center", padding: "4px" }}
              >
                <FiExternalLink size={12} />
              </a>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}

// ── Provider user list table ──────────────────────────────────────────────────

const UserList = ({
  users,
  showMatchCount,
}: {
  users: ProviderUserSummary[]
  showMatchCount?: boolean
}) => {
  if (users.length === 0) {
    return (
      <Text fontSize="sm" color="green.600" px={4} py={3}>
        ✓ All clear
      </Text>
    )
  }
  return (
    <Table.Root size="sm">
      <Table.Body>
        {users.map((u) => (
          <Table.Row key={u.id}>
            <Table.Cell>
              <Text fontSize="sm" fontWeight="medium">{u.displayName}</Text>
              {u.email && <Text fontSize="xs" color="gray.400">{u.email}</Text>}
            </Table.Cell>
            {showMatchCount ? (
              <Table.Cell textAlign="right">
                <Badge colorPalette="red" size="sm">
                  {u.matchingClientCount} matching clients
                </Badge>
              </Table.Cell>
            ) : (
              <Table.Cell>
                {u.userTypeName ? (
                  <Badge colorPalette="blue" variant="subtle" size="sm">{u.userTypeName}</Badge>
                ) : (
                  <Text fontSize="xs" color="gray.400">No type assigned</Text>
                )}
              </Table.Cell>
            )}
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

function ProviderDashboard() {
  const providerId = sessionStorage.getItem("selectedProviderId") ?? ""
  const providerName = sessionStorage.getItem("selectedProviderName") ?? "Provider"

  const { data: stats, isLoading } = useQuery({
    queryKey: ["providerAdminDashboardStats", providerId],
    queryFn: () => Dashboard.getProviderDashboardStats({ query: { providerId } }),
    enabled: !!providerId,
    staleTime: 60_000,
  })

  return (
    <Container maxW="full">
      <Box pt={12} mb={6}>
        <Heading size="lg" mb={1}>{providerName} — Provider Dashboard</Heading>
        <Text color="gray.500" fontSize="sm">
          Overview of clients and provider users that may need attention.
        </Text>
      </Box>

      {!providerId && (
        <Text color="red.500">No provider selected.</Text>
      )}

      {isLoading && <Spinner />}

      {stats && (
        <Tabs.Root defaultValue="clients" lazyMount>
          <Tabs.List mb={6}>
            <Tabs.Trigger value="clients">Clients</Tabs.Trigger>
            <Tabs.Trigger value="users">Users</Tabs.Trigger>
          </Tabs.List>

          {/* ── Clients tab ── */}
          <Tabs.Content value="clients">
            <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(3, 1fr)" }} gap={4} mb={8}>
              <GridItem>
                <StatCard label="Total Clients" value={stats.totalClients} icon={FiUsers} colorPalette="blue" />
              </GridItem>
              <GridItem>
                <StatCard label="No Budget" value={stats.clientsNoBudget.length} icon={FiAlertCircle} colorPalette="orange" />
              </GridItem>
              <GridItem>
                <StatCard label="No Services Set" value={stats.clientsNoServices.length} icon={FiBriefcase} colorPalette="red" />
              </GridItem>
              <GridItem>
                <StatCard label="Overdue Services" value={stats.clientsNoRecentService.length} icon={FiAlertCircle} colorPalette="orange" />
              </GridItem>
              <GridItem>
                <StatCard label="Budget at Risk" value={stats.clientsBudgetAtRisk.length} icon={FiBriefcase} colorPalette="red" />
              </GridItem>
              <GridItem>
                <StatCard label="Birthdays (7 days)" value={stats.clientBirthdays.length} icon={FiCalendar} colorPalette="purple" />
              </GridItem>
            </Grid>

            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4} mb={4}>
              <GridItem>
                <AlertSection title={`No Budget Assigned (${stats.clientsNoBudget.length})`} icon={FiAlertCircle} colorPalette="orange">
                  <ClientList clients={stats.clientsNoBudget} />
                </AlertSection>
              </GridItem>
              <GridItem>
                <AlertSection title={`No Services Set (${stats.clientsNoServices.length})`} icon={FiBriefcase} colorPalette="red">
                  <ClientList clients={stats.clientsNoServices} />
                </AlertSection>
              </GridItem>
            </Grid>

            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4} mb={4}>
              <GridItem>
                <AlertSection title={`Overdue Services (${stats.clientsNoRecentService.length})`} icon={FiAlertCircle} colorPalette="orange">
                  <NoRecentServiceList clients={stats.clientsNoRecentService} />
                </AlertSection>
              </GridItem>
              <GridItem>
                <AlertSection title={`Budget at Risk (${stats.clientsBudgetAtRisk.length})`} icon={FiBriefcase} colorPalette="red">
                  <BudgetRiskList clients={stats.clientsBudgetAtRisk} />
                </AlertSection>
              </GridItem>
            </Grid>

            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
              <GridItem>
                <AlertSection
                  title={`Birthdays in Next 7 Days (${stats.clientBirthdays.length})`}
                  icon={FiCalendar}
                  colorPalette="purple"
                >
                  {stats.clientBirthdays.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" px={4} py={3}>No upcoming birthdays</Text>
                  ) : (
                    <Table.Root size="sm">
                      <Table.Body>
                        {stats.clientBirthdays.map((c) => (
                          <Table.Row key={c.id}>
                            <Table.Cell>
                              <Text fontSize="sm" fontWeight="medium">{c.displayName}</Text>
                            </Table.Cell>
                            <Table.Cell textAlign="right">
                              <Badge
                                colorPalette={c.daysUntil === 0 ? "green" : "purple"}
                                size="sm"
                              >
                                {c.daysUntil === 0 ? "Today! 🎂" : `in ${c.daysUntil}d`}
                              </Badge>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  )}
                </AlertSection>
              </GridItem>
            </Grid>
          </Tabs.Content>

          {/* ── Users tab ── */}
          <Tabs.Content value="users">
            <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(3, 1fr)" }} gap={4} mb={8}>
              <GridItem>
                <StatCard label="Total Provider Users" value={stats.totalProviderUsers} icon={FiUsers} colorPalette="gray" />
              </GridItem>
              <GridItem>
                <StatCard label="No User Type" value={stats.usersNoUserType.length} icon={FiAlertCircle} colorPalette="orange" />
              </GridItem>
              <GridItem>
                <StatCard label="No Matching Clients" value={stats.usersNoMatchingClients.length} icon={FiBriefcase} colorPalette="red" />
              </GridItem>
            </Grid>

            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
              <GridItem>
                <AlertSection
                  title={`No User Type Assigned (${stats.usersNoUserType.length})`}
                  icon={FiUsers}
                  colorPalette="orange"
                >
                  <UserList users={stats.usersNoUserType} />
                </AlertSection>
              </GridItem>
              <GridItem>
                <AlertSection
                  title={`No Matching Clients for Skills (${stats.usersNoMatchingClients.length})`}
                  icon={FiAlertCircle}
                  colorPalette="red"
                >
                  <UserList users={stats.usersNoMatchingClients} showMatchCount />
                </AlertSection>
              </GridItem>
            </Grid>
          </Tabs.Content>
        </Tabs.Root>
      )}
    </Container>
  )
}
